import { useState, useEffect, useCallback, useMemo } from "react";
import { listFaqs, createFaq, updateFaq, deleteFaq } from "../../../api/faqs";
import type { Faq } from "../../../types/faq";
import { Modal, Button, Toast, useToast } from "@hiya/ui";
import { KebabMenu } from "../../../components/KebabMenu";
import iconSearch from "../../../assets/icon-search.svg";
import closeSvg from "../../../assets/icon-close.svg";

function answerSummary(faq: Faq): string {
  return faq.answer.map((block) => block.content).join(" ");
}

interface SidePanelProps {
  open: boolean;
  title: string;
  question: string;
  answer: string;
  saving: boolean;
  onQuestionChange: (v: string) => void;
  onAnswerChange: (v: string) => void;
  onSave: () => void;
  onClose: () => void;
}

function SidePanel({
  open,
  title,
  question,
  answer,
  saving,
  onQuestionChange,
  onAnswerChange,
  onSave,
  onClose,
}: Readonly<SidePanelProps>) {
  if (!open) return null;

  const canSave = question.trim().length > 0 && answer.trim().length > 0 && !saving;

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-overlay border-none cursor-default"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
        tabIndex={-1}
        aria-label="Close panel"
      />
      <div className="relative w-[480px] h-full bg-bg-primary shadow-lg flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stroke-primary">
          <h2 className="text-xl font-semibold text-text-primary">{title}</h2>
          <button onClick={onClose} aria-label="Close">
            <img src={closeSvg} alt="" width={24} height={24} />
          </button>
        </div>

        <div className="flex-1 flex flex-col gap-4 p-6 overflow-y-auto">
          <div className="flex flex-col gap-2">
            <label htmlFor="faq-question" className="text-sm font-medium text-text-primary">
              Question
            </label>
            <input
              id="faq-question"
              type="text"
              value={question}
              onChange={(e) => onQuestionChange(e.target.value)}
              placeholder="Enter question"
              className="h-[51px] rounded-2xl border border-stroke-primary px-4 text-sm text-text-primary bg-bg-primary focus:outline-none focus:border-brand-brown"
            />
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <label htmlFor="faq-answer" className="text-sm font-medium text-text-primary">
              Answer
            </label>
            <textarea
              id="faq-answer"
              value={answer}
              onChange={(e) => onAnswerChange(e.target.value)}
              placeholder="Enter answer"
              className="flex-1 min-h-[120px] rounded-2xl border border-stroke-primary p-4 text-sm text-text-primary bg-bg-primary resize-none focus:outline-none focus:border-brand-brown"
            />
          </div>
        </div>

        <div className="px-6 pb-6">
          <button
            onClick={onSave}
            disabled={!canSave}
            className={`w-full h-[51px] rounded-2xl font-semibold text-base text-text-btn transition-colors ${
              canSave
                ? "bg-btn-primary cursor-pointer"
                : "bg-btn-primary-disabled cursor-not-allowed"
            }`}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function FAQPage() {
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<Faq | null>(null);
  const [editTarget, setEditTarget] = useState<Faq | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<"create" | "edit">("create");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { toast, showToast } = useToast();

  const filtered = useMemo(() => {
    if (!search.trim()) return faqs;
    const q = search.toLowerCase();
    return faqs.filter((f) => f.question.toLowerCase().includes(q));
  }, [faqs, search]);

  const loadFaqs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listFaqs();
      setFaqs(data);
    } catch {
      showToast("Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadFaqs();
  }, [loadFaqs]);

  function openCreatePanel() {
    setPanelMode("create");
    setEditTarget(null);
    setQuestion("");
    setAnswer("");
    setPanelOpen(true);
  }

  function openEditPanel(faq: Faq) {
    setPanelMode("edit");
    setEditTarget(faq);
    setQuestion(faq.question);
    setAnswer(faq.answer.map((b) => b.content).join("\n"));
    setPanelOpen(true);
  }

  function closePanel() {
    setPanelOpen(false);
    setEditTarget(null);
    setQuestion("");
    setAnswer("");
  }

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    try {
      const answerBlocks = [{ type: "text" as const, content: answer.trim() }];
      if (panelMode === "create") {
        const created = await createFaq({ question: question.trim(), answer: answerBlocks });
        setFaqs((prev) => [created, ...prev]);
        showToast("New question published");
      } else if (editTarget) {
        const updated = await updateFaq(editTarget.id, {
          question: question.trim(),
          answer: answerBlocks,
        });
        setFaqs((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
        showToast("Question edited");
      }
      closePanel();
    } catch {
      showToast(panelMode === "create" ? "Failed to create FAQ" : "Failed to update FAQ");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await deleteFaq(deleteTarget.id);
      setFaqs((prev) => prev.filter((f) => f.id !== deleteTarget.id));
      showToast("Question deleted");
    } catch {
      showToast("Failed to delete question");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }

  return (
    <div className="overflow-x-hidden">
      <Toast message={toast.message} visible={toast.visible} />

      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <img
            src={iconSearch}
            alt=""
            width={20}
            height={20}
            className="absolute left-4 top-1/2 -translate-y-1/2"
          />
          <input
            type="search"
            aria-label="Search FAQ"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search FAQ"
            className="w-full h-[51px] pl-12 pr-4 rounded-2xl border border-stroke-primary bg-bg-primary text-base text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-brand-brown"
          />
        </div>
        <button
          type="button"
          onClick={openCreatePanel}
          className="h-[51px] px-6 rounded-2xl bg-bg-secondary text-brand-brown text-base font-semibold flex items-center justify-center gap-1 whitespace-nowrap"
        >
          + Add Question
        </button>
      </div>

      {loading && (
        <div className="flex flex-col">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-[60px] bg-bg-secondary animate-pulse mb-4 rounded-xl"
            />
          ))}
        </div>
      )}

      {!loading && faqs.length === 0 && (
        <div className="text-center py-20">
          <p className="text-text-secondary text-base">No questions yet</p>
        </div>
      )}

      {!loading && faqs.length > 0 && filtered.length === 0 && (
        <div className="text-center py-20">
          <p className="text-text-secondary text-base">No results found</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="flex flex-col">
          {filtered.map((faq, index) => (
            <div
              key={faq.id}
              className={`flex items-center gap-3 py-4 ${
                index < filtered.length - 1 ? "border-b border-stroke-primary" : ""
              }`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-text-primary">{faq.question}</p>
                <p className="text-sm text-text-secondary line-clamp-2">{answerSummary(faq)}</p>
              </div>
              <KebabMenu
                onEdit={() => openEditPanel(faq)}
                onDelete={() => setDeleteTarget(faq)}
              />
            </div>
          ))}
        </div>
      )}

      <SidePanel
        open={panelOpen}
        title={panelMode === "create" ? "New Question" : "Edit Question"}
        question={question}
        answer={answer}
        saving={saving}
        onQuestionChange={setQuestion}
        onAnswerChange={setAnswer}
        onSave={handleSave}
        onClose={closePanel}
      />

      <Modal
        open={deleteTarget !== null}
        title="Delete question"
        onClose={() => setDeleteTarget(null)}
      >
        <p className="text-sm text-text-primary leading-[1.5]">
          Are you sure you want to delete this question? It will be deleted from the app
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete question"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
